import { Phone, ShieldCheck, Award, MapPin } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { trackCallConversion } from "@/lib/tracking";

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="top" className="relative border-b border-zinc-900 bg-[#F4F4F5]">
      <div className="bg-grid absolute inset-0 opacity-60 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* LEFT */}
        <div className="lg:col-span-7 flex flex-col justify-center">
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <span
              data-testid={HERO.badgeLicensed}
              className="inline-flex items-center gap-2 bg-[#0B2B40] text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono font-semibold"
            >
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
              Licensed & Insured
            </span>
            <span
              data-testid={HERO.badgeSpanish}
              className="inline-flex items-center gap-2 bg-[#F59E0B] text-black px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono font-semibold border border-black"
            >
              Se Habla Español
            </span>
            <span
              data-testid={HERO.badgeLocal}
              className="inline-flex items-center gap-2 bg-white text-black px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-mono font-semibold border border-black"
            >
              <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} />
              Pittsburgh, PA
            </span>
          </div>

          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] mb-5 font-bold">
            EST. PITTSBURGH · INDEX #001
          </p>

          <h1 className="font-display uppercase font-semibold leading-[0.9] tracking-tighter text-[12vw] sm:text-7xl lg:text-[7.2rem]">
            We Got <br />
            <span className="text-[#D9480F]">A Guy</span> <br />
            For That.
          </h1>

          <p className="mt-8 text-base md:text-lg text-zinc-700 max-w-xl leading-relaxed">
            Blue Collar World Pest Control delivers no-nonsense extermination across
            <span className="font-semibold text-zinc-900"> households, parks, and schools</span>.
            Specialists in wood-destroying insects, wildlife removal, bed bugs and mosquitoes.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="tel:4847643392"
              data-testid={HERO.callBtn}
              onClick={() => trackCallConversion("hero_call_btn")}
              className="inline-flex items-center gap-3 bg-[#D9480F] text-white px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#E8590C] transition-colors hard-shadow"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Call 484-764-3392
            </a>
            <button
              data-testid={HERO.inspectionBtn}
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center gap-3 bg-transparent border-2 border-[#0B2B40] text-[#0B2B40] px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#0B2B40] hover:text-white transition-colors"
            >
              Get Free Inspection →
            </button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-mono uppercase tracking-[0.18em] text-zinc-600">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-[#D9480F]" strokeWidth={2.5} />
              PA License #BU15680
            </div>
            <div>State Certified Applicator</div>
            <div>EPA Compliant Methods</div>
            <div>Family & Pet Safe Options</div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-5 relative">
          <div className="relative w-full aspect-[4/5] border-2 border-zinc-900 overflow-hidden hard-shadow">
            <img
              src="https://images.pexels.com/photos/195220/pexels-photo-195220.jpeg"
              alt="Pittsburgh skyline served by Blue Collar World Pest Control"
              className="w-full h-full object-cover grayscale-[20%] contrast-110"
            />
            <div className="absolute inset-0 bg-[#0B2B40]/10 mix-blend-multiply" />
            <div className="absolute bottom-0 left-0 right-0 bg-[#0B2B40] text-white px-5 py-4 border-t-2 border-zinc-900">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F59E0B]">
                    Serving Greater
                  </div>
                  <div className="font-display uppercase text-xl tracking-tight">
                    Pittsburgh & Allegheny County
                  </div>
                </div>
                <div className="font-display text-3xl text-[#F59E0B]">15+</div>
              </div>
            </div>
          </div>

          <div className="hidden md:block absolute -bottom-6 -left-6 bg-[#F59E0B] border-2 border-black px-4 py-3 max-w-[200px]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold">
              24/7 Emergency
            </div>
            <div className="font-display text-lg leading-tight">
              We pick up. Always.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
